// CastingCallPublicPage.js - Complete Mobile & Desktop Optimized Version
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import client from "../../../../API";
import { useQuery } from "react-query";
import {
  Typography,
  Box,
  Container,
  Chip,
  Stack,
  Button,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Divider,
  Card,
  CardContent,
  Grid,
  Paper,
  ImageList,
  ImageListItem,
  IconButton,
  Badge,
  Slide,
  Collapse,
  SwipeableDrawer,
  Fab,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import {
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WorkOutline as WorkIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Launch as LaunchIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  VideoLibrary as VideoIcon,
  Lock as LockIcon,
  Share as ShareIcon,
  Dashboard as DashboardIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import useAuth from "../../../../Hooks/use-auth";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import HomeHeader from "../../../../Components/HomeHeader";
import PageTitle from "../../../../Components/PageTitle";
import routes from "../../../../Config/routes";
import CastingCallApplication from "./castingcallapplicationdialog.js";

// ───────── Constants ─────────
const APPSHELL_HEIGHT = 86; // Height of AppShell bottom navigation
const BOTTOM_PADDING = 12; // Extra padding for comfort

// ───────── Animations ─────────
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.6);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// ───────── Mobile Styled Components ─────────
const MobileHeroSection = styled(Box)(({ theme, themeColors, hasHeaderImage }) => {
  const primaryColor = themeColors?.primary || theme.palette.primary.main;
  const secondaryColor = themeColors?.secondary || theme.palette.secondary.main;
  
  return {
    position: "relative",
    overflow: "hidden",
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    background: hasHeaderImage 
      ? `linear-gradient(180deg, ${alpha(primaryColor, 0.85)} 0%, ${alpha(secondaryColor, 0.92)} 100%)`
      : `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    [theme.breakpoints.up('md')]: {
      minHeight: 420,
    },
  };
});

const MobileHeaderImage = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  "&::after": {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)',
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const MobileAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  background: scrolled 
    ? theme.palette.background.paper
    : 'transparent',
  boxShadow: scrolled ? theme.shadows[4] : 'none',
  transition: 'all 0.3s ease',
  backdropFilter: scrolled ? 'blur(20px)' : 'none',
}));

const StickyApplyButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: APPSHELL_HEIGHT + BOTTOM_PADDING,
  right: theme.spacing(2),
  left: theme.spacing(2),
  borderRadius: 16,
  height: 56,
  width: 'auto',
  maxWidth: 400,
  margin: '0 auto',
  zIndex: 999, // Below AppShell (1000+)
  boxShadow: theme.shadows[12],
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  fontSize: '1.05rem',
  fontWeight: 700,
  textTransform: 'none',
  animation: `${slideUp} 0.4s ease-out`,
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[20],
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const MobileCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  animation: `${scaleIn} 0.4s ease-out`,
}));

const MobileStatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const SwipeableImageGallery = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(0, 2, 2, 2),
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
}));

const GalleryImageCard = styled(Box)(({ theme }) => ({
  minWidth: '85%',
  scrollSnapAlign: 'center',
  borderRadius: 20,
  overflow: 'hidden',
  position: 'relative',
  paddingTop: '100%',
  boxShadow: theme.shadows[4],
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const BottomSheet = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    maxHeight: '90vh',
    overflow: 'visible',
  },
}));

const BottomSheetHandle = styled(Box)(({ theme }) => ({
  width: 40,
  height: 5,
  borderRadius: 3,
  backgroundColor: theme.palette.divider,
  margin: '12px auto 20px',
}));

const MobileButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(1.75, 3),
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1.05rem",
  transition: "all 0.2s ease",
  '&:active': {
    transform: 'scale(0.97)',
  },
}));

// ───────── Desktop Styled Components ─────────
const ModernGradientHeader = styled(Box)(({ theme, themeColors, hasHeaderImage }) => {
  const primaryColor = themeColors?.primary || theme.palette.primary.main;
  const secondaryColor = themeColors?.secondary || theme.palette.secondary.main;
  
  return {
    background: hasHeaderImage 
      ? `linear-gradient(135deg, ${alpha(primaryColor, 0.92)} 0%, ${alpha(secondaryColor, 0.92)} 100%)`
      : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    position: "relative",
    overflow: "hidden",
    paddingTop: 80,
    minHeight: 420,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      paddingTop: 70,
      minHeight: 360,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "linear-gradient(45deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
      pointerEvents: "none",
      zIndex: 2,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "50%",
      background: "linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%)",
      pointerEvents: "none",
      zIndex: 2,
    },
  };
});

const HeaderImageBackground = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: theme.shadows[3],
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[10],
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  borderRadius: 10,
  fontWeight: 600,
  fontSize: "0.875rem",
  height: 34,
  boxShadow: theme.shadows[2],
  backdropFilter: "blur(10px)",
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 20,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2.5),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.primary.main,
  },
}));

const GalleryImage = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  paddingTop: "75%",
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: theme.shadows[2],
  "&:hover": {
    transform: "scale(1.03)",
    boxShadow: theme.shadows[12],
  },
  "& img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const AssetCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
    borderColor: theme.palette.primary.main,
  },
}));

const AssetPreview = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 180,
  position: "relative",
  overflow: "hidden",
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 24,
    padding: theme.spacing(2),
    boxShadow: theme.shadows[24],
  },
}));

// ───────── Shared Components ─────────
const ExpandableSection = ({ title, children, defaultExpanded = false, icon }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();

  return (
    <MobileCard>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 3, pb: 3 }}>
          {children}
        </Box>
      </Collapse>
    </MobileCard>
  );
};

const PlatformIcon = ({ platform }) => {
  const iconMap = {
    instagram: <InstagramIcon />,
    youtube: <YouTubeIcon />,
    facebook: <FacebookIcon />,
    twitter: <TwitterIcon />,
    linkedin: <LinkedInIcon />,
    tiktok: <Box component="span">📱</Box>,
  };
  return iconMap[platform.toLowerCase()] || <WorkIcon />;
};

// ────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────────────────
export default function CastingCallPublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { getCurrrentUser, isAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized } = useCreatorAuth();

  const [scrolled, setScrolled] = useState(false);
  const [showApplyButton, setShowApplyButton] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginSheetOpen, setLoginSheetOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successSheetOpen, setSuccessSheetOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const applyCardRef = useRef(null);

  // Scroll handler for mobile app bar
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);

      // Show sticky apply button when apply card is out of view
      if (applyCardRef.current) {
        const rect = applyCardRef.current.getBoundingClientRect();
        setShowApplyButton(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  /* ─── Fetch casting call ─── */
  const {
    data: castingCall,
    isLoading,
    refetch,
  } = useQuery(
    ["castingCallPublic", slug],
    () => client.castingCalls.getPublic(slug),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  /* ─── Fetch organizers ─── */
  const {
    data: organizers,
    isLoading: organizersLoading,
  } = useQuery(
    ["castingCallOrganizers", slug],
    () => client.castingCalls.getPublicOrganizers(slug),
    {
      enabled: !!castingCall,
      refetchOnWindowFocus: false,
    }
  );

  // Image viewer handlers
  const allImages = castingCall ? [
    ...(castingCall.header_image ? [castingCall.header_image] : []),
    ...(castingCall.supporting_images || [])
  ] : [];

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setImageViewerOpen(true);
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const getFileIcon = (type) => {
    if (type?.includes('image')) return <ImageIcon sx={{ fontSize: isMobile ? 24 : 28, color: 'primary.main' }} />;
    if (type?.includes('video')) return <VideoIcon sx={{ fontSize: isMobile ? 24 : 28, color: 'primary.main' }} />;
    return <FileIcon sx={{ fontSize: isMobile ? 24 : 28, color: 'primary.main' }} />;
  };

  const handleShareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: castingCall.brief,
        text: castingCall.description,
        url: url,
      }).catch(() => {
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
    if (isMobile) {
      setShareSheetOpen(false);
    } else {
      setShareDialogOpen(false);
    }
  };

  /* ─── Loading state ─── */
  if (isLoading) {
    return (
      <>
        {!isMobile && <HomeHeader />}
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "white", mb: 2 }} size={60} thickness={4} />
            <Typography variant="h6" sx={{ color: "white", fontWeight: 500 }}>
              Loading...
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  if (!castingCall) {
    return (
      <>
        {!isMobile && <HomeHeader />}
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: isMobile ? 8 : 10,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Casting call not found.
          </Typography>
        </Box>
      </>
    );
  }

  const isCreatorCall = castingCall.call_type === "creator";
  const hasApplied = castingCall.has_applied || false;
  const isActive = castingCall.status === "ACTIVE";
  const hasHeaderImage = Boolean(castingCall.header_image);
  const hasSupportingImages = castingCall.supporting_images && castingCall.supporting_images.length > 0;
  const isOwner = castingCall.is_owner || false;

  const getStatusChip = (mobile = false) => {
    if (!isActive) {
      return (
        <Chip 
          label="Closed" 
          size={mobile ? "small" : "medium"}
          sx={{ 
            bgcolor: alpha(theme.palette.grey[500], mobile ? 0.2 : 0.15),
            color: theme.palette.text.secondary,
            fontWeight: 700,
            height: mobile ? 28 : 34,
            fontSize: mobile ? '0.75rem' : '0.875rem',
            ...(mobile ? {} : {
              border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
            }),
          }} 
        />
      );
    }
    return (
      <Chip
        label="Open"
        size={mobile ? "small" : "medium"}
        icon={<LaunchIcon sx={{ fontSize: mobile ? 14 : 'small' }} />}
        sx={{
          bgcolor: alpha(theme.palette.success.main, mobile ? 0.2 : 0.15),
          color: theme.palette.success.main,
          fontWeight: 700,
          height: mobile ? 28 : 34,
          fontSize: mobile ? '0.75rem' : '0.875rem',
          ...(mobile ? {} : {
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          }),
        }}
      />
    );
  };

  const getCallTypeBadge = () => {
    if (isCreatorCall) {
      return (
        <Chip
          label="Creator Opportunity"
          sx={{ 
            fontWeight: 600, 
            fontSize: "0.875rem", 
            height: 34,
            borderRadius: 10,
            bgcolor: alpha(theme.palette.secondary.main, 0.15),
            color: theme.palette.secondary.main,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
          }}
        />
      );
    }
    return (
      <Chip 
        label="General Work" 
        sx={{ 
          fontWeight: 600, 
          fontSize: "0.875rem", 
          height: 34,
          borderRadius: 10,
          bgcolor: alpha(theme.palette.primary.main, 0.15),
          color: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }} 
      />
    );
  };

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  MOBILE RENDER                                                           */
  /* ───────────────────────────────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <>
        <PageTitle title={`${castingCall.brief} - Blitz`} />

        {/* Mobile App Bar */}
        <MobileAppBar position="fixed" scrolled={scrolled} elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => navigate(-1)}
              sx={{ 
                color: scrolled ? 'text.primary' : 'white',
                mr: 2,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Collapse in={scrolled} orientation="horizontal">
              <Typography 
                variant="subtitle1" 
                fontWeight={700}
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: 200, sm: 300 },
                }}
              >
                {castingCall.brief}
              </Typography>
            </Collapse>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              onClick={() => setShareSheetOpen(true)}
              sx={{ color: scrolled ? 'text.primary' : 'white' }}
            >
              <ShareIcon />
            </IconButton>
          </Toolbar>
        </MobileAppBar>

        {/* Hero Section */}
        <MobileHeroSection 
          themeColors={castingCall.theme_colors}
          hasHeaderImage={hasHeaderImage}
        >
          {hasHeaderImage && (
            <MobileHeaderImage>
              <img src={castingCall.header_image} alt="Header" />
            </MobileHeaderImage>
          )}

          <Box sx={{ 
            position: 'relative', 
            zIndex: 2, 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pt: 10,
            px: 3,
            pb: 4,
          }}>
            {/* Status Chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
              {getStatusChip(true)}
              <Chip
                label={isCreatorCall ? "Creator" : "General"}
                size="small"
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 700,
                  height: 28,
                  fontSize: '0.75rem',
                  backdropFilter: 'blur(10px)',
                }}
              />
              {isOwner && (
                <Chip
                  label="Your Post"
                  size="small"
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: 'white',
                    fontWeight: 700,
                    height: 28,
                    fontSize: '0.75rem',
                    backdropFilter: 'blur(10px)',
                  }}
                />
              )}
            </Stack>

            {/* Title */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "white",
                fontSize: { xs: "1.75rem", sm: "2.25rem" },
                lineHeight: 1.2,
                mb: 2,
                textShadow: "0 2px 20px rgba(0,0,0,0.6)",
              }}
            >
              {castingCall.brief}
            </Typography>

            {/* Poster Info */}
            {castingCall.poster && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={castingCall.poster.pfphref || ""}
                  sx={{
                    width: 48,
                    height: 48,
                    border: "2px solid rgba(255,255,255,0.5)",
                  }}
                />
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: "rgba(255,255,255,0.8)",
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Posted By
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      textShadow: "0 1px 8px rgba(0,0,0,0.6)",
                    }}
                  >
                    {castingCall.poster.display_name}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </MobileHeroSection>

        {/* Content */}
        <Box sx={{ 
          bgcolor: "background.default", 
          pb: APPSHELL_HEIGHT + BOTTOM_PADDING + 20, // Account for AppShell
        }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
            {/* Quick Stats */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MobileStatCard elevation={0}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MoneyIcon sx={{ fontSize: 28, color: 'success.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Budget
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        ${castingCall.budget?.toLocaleString() || "TBD"}
                      </Typography>
                    </Box>
                  </MobileStatCard>
                </Grid>
                <Grid item xs={6}>
                  <MobileStatCard elevation={0}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 28, color: 'info.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {isOwner ? 'Applied' : 'Applicants'}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {castingCall.applicant_count || 0}
                      </Typography>
                    </Box>
                  </MobileStatCard>
                </Grid>
              </Grid>
            </Stack>

            {/* Apply Card - Ref for sticky button logic */}
            <div ref={applyCardRef}>
              {isOwner ? (
                <MobileCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 42, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                        Your Casting Call
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                        View and manage applications from your dashboard
                      </Typography>
                      <Stack spacing={2}>
                        <MobileButton
                          variant="contained"
                          fullWidth
                          startIcon={<DashboardIcon />}
                          onClick={() => navigate(`/castingcall/${castingCall.id}/manage`)}
                        >
                          Manage Applications
                        </MobileButton>
                        <MobileButton
                          variant="outlined"
                          fullWidth
                          startIcon={<ShareIcon />}
                          onClick={() => setShareSheetOpen(true)}
                        >
                          Share
                        </MobileButton>
                      </Stack>
                    </Box>
                  </CardContent>
                </MobileCard>
              ) : (
                <MobileCard>
                  <CardContent sx={{ p: 3 }}>
                    <CastingCallApplication
                      castingCall={castingCall}
                      isAuthenticated={isAuthorized || isCreatorAuthorized}
                      isCreatorAuthorized={isCreatorAuthorized}
                      hasApplied={hasApplied}
                      onSuccess={() => {
                        setSuccessSheetOpen(true);
                        refetch();
                      }}
                      onLoginRequired={() => setLoginSheetOpen(true)}
                    />
                  </CardContent>
                </MobileCard>
              )}
            </div>

            {/* Gallery */}
            {hasSupportingImages && (
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Gallery
                  </Typography>
                  <Chip 
                    label={castingCall.supporting_images.length}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <SwipeableImageGallery>
                  {castingCall.supporting_images.map((image, index) => (
                    <GalleryImageCard 
                      key={index}
                      onClick={() => handleImageClick(index + (hasHeaderImage ? 1 : 0))}
                    >
                      <img src={image} alt={`Gallery ${index + 1}`} loading="lazy" />
                    </GalleryImageCard>
                  ))}
                </SwipeableImageGallery>
              </Box>
            )}

            {/* About */}
            <ExpandableSection
              title="About This Opportunity"
              icon={<FileIcon sx={{ color: 'primary.main' }} />}
              defaultExpanded={true}
            >
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                }}
              >
                {castingCall.description || "No additional details provided."}
              </Typography>
            </ExpandableSection>

            {/* Platforms/Work Types */}
            {((isCreatorCall && castingCall.platforms && castingCall.platforms.length > 0) ||
              (!isCreatorCall && castingCall.work_types && castingCall.work_types.length > 0)) && (
              <ExpandableSection
                title={isCreatorCall ? "Platforms" : "Work Types"}
                icon={<WorkIcon sx={{ color: 'primary.main' }} />}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {(isCreatorCall ? castingCall.platforms : castingCall.work_types).map((item) => (
                    <Chip
                      key={item}
                      icon={isCreatorCall ? <PlatformIcon platform={item} /> : <WorkIcon />}
                      label={isCreatorCall ? item : item.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 2.5,
                        height: 36,
                      }}
                    />
                  ))}
                </Stack>
              </ExpandableSection>
            )}

            {/* Project Assets */}
            {!organizersLoading && organizers && organizers.total_count > 0 && (
              <ExpandableSection
                title="Project Assets"
                icon={<FolderIcon sx={{ color: 'primary.main' }} />}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Shared deliverables and collaboration files
                </Typography>

                <Stack spacing={3}>
                  {organizers.organizers.map((org) => (
                    <Box key={org.id}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                        {org.name}
                      </Typography>

                      {org.assets_preview.length > 0 ? (
                        <Stack spacing={2}>
                          {org.assets_preview.map((asset) => (
                            <Paper
                              key={asset.id}
                              elevation={0}
                              sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: `1px solid ${theme.palette.divider}`,
                                '&:active': {
                                  transform: 'scale(0.98)',
                                },
                                transition: 'transform 0.2s',
                              }}
                              onClick={() => {
                                if (!hasApplied && !isOwner) {
                                  alert('Apply to view full assets');
                                }
                              }}
                            >
                              {asset.type?.includes('image') && asset.url ? (
                                <>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: 140,
                                      position: 'relative',
                                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                                    }}
                                  >
                                    <img
                                      src={asset.url}
                                      alt={asset.name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {asset.name}
                                    </Typography>
                                  </Box>
                                </>
                              ) : (
                                <Box sx={{ p: 2 }}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {getFileIcon(asset.type)}
                                    </Box>
                                    <Typography variant="body2" fontWeight={600} flex={1}>
                                      {asset.name}
                                    </Typography>
                                  </Stack>
                                </Box>
                              )}
                            </Paper>
                          ))}

                          {org.file_count > org.assets_preview.length && (
                            <Typography variant="caption" color="text.secondary" align="center">
                              +{org.file_count - org.assets_preview.length} more files
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.grey[500], 0.04),
                            borderRadius: 3,
                            border: `1px dashed ${theme.palette.divider}`,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {org.file_count} file(s) available {isOwner ? 'in organizer' : 'after applying'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>

                {!hasApplied && !isOwner && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.info.main, 0.06),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Apply to access all {organizers.total_assets} files
                    </Typography>
                  </Box>
                )}
              </ExpandableSection>
            )}
          </Container>
        </Box>

        {/* Sticky Apply Button */}
        {showApplyButton && !isOwner && !hasApplied && isActive && (
          <StickyApplyButton
            variant="extended"
            onClick={() => {
              if (!isAuthorized && !isCreatorAuthorized) {
                setLoginSheetOpen(true);
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <SendIcon sx={{ mr: 1 }} />
            Apply Now
          </StickyApplyButton>
        )}

        {/* Login Bottom Sheet */}
        <BottomSheet
          anchor="bottom"
          open={loginSheetOpen}
          onClose={() => setLoginSheetOpen(false)}
          onOpen={() => setLoginSheetOpen(true)}
          disableSwipeToOpen
        >
          <Box sx={{ p: 3 }}>
            <BottomSheetHandle />
            
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <LoginIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Join Blitz to Apply
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                Connect with opportunities in the creator economy
              </Typography>
            </Box>

            <Stack spacing={2}>
              <MobileButton
                onClick={() => navigate(
                  castingCall.global_user_id 
                    ? `/invite/${castingCall.global_user_id}` 
                    : routes.onboard
                )}
                variant="contained"
                fullWidth
                size="large"
                startIcon={<PersonAddIcon />}
              >
                Create Account
              </MobileButton>
              <MobileButton
                onClick={() => navigate(routes.login)}
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<LoginIcon />}
              >
                Log In
              </MobileButton>
            </Stack>
          </Box>
        </BottomSheet>

        {/* Success Bottom Sheet */}
        <BottomSheet
          anchor="bottom"
          open={successSheetOpen}
          onClose={() => setSuccessSheetOpen(false)}
          onOpen={() => setSuccessSheetOpen(true)}
          disableSwipeToOpen
        >
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <BottomSheetHandle />
            
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.success.main, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            </Box>
            
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Application Submitted!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
              Your application has been submitted successfully. The poster will review and get back to you soon.
            </Typography>

            <MobileButton
              onClick={() => setSuccessSheetOpen(false)}
              variant="contained"
              color="success"
              fullWidth
              size="large"
            >
              Got It!
            </MobileButton>
          </Box>
        </BottomSheet>

        {/* Share Bottom Sheet */}
        <BottomSheet
          anchor="bottom"
          open={shareSheetOpen}
          onClose={() => setShareSheetOpen(false)}
          onOpen={() => setShareSheetOpen(true)}
          disableSwipeToOpen
        >
          <Box sx={{ p: 3 }}>
            <BottomSheetHandle />
            
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Share This Opportunity
            </Typography>

            <Stack spacing={2}>
              <MobileButton
                onClick={handleShareLink}
                variant="contained"
                fullWidth
                startIcon={<ShareIcon />}
              >
                Share Link
              </MobileButton>
              <MobileButton
                onClick={() => setShareSheetOpen(false)}
                variant="outlined"
                fullWidth
              >
                Cancel
              </MobileButton>
            </Stack>
          </Box>
        </BottomSheet>

        {/* Image Viewer */}
        <Dialog
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          fullScreen
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0,0,0,0.98)',
            },
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton
              onClick={() => setImageViewerOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                zIndex: 10,
              }}
            >
              <CloseIcon />
            </IconButton>

            {allImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePreviousImage}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
              </>
            )}

            <Box
              component="img"
              src={allImages[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                px: 2,
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.8)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 3,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {currentImageIndex + 1} / {allImages.length}
              </Typography>
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  DESKTOP RENDER                                                          */
  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <HomeHeader />
      <PageTitle title={`${castingCall.brief} - Blitz`} />

      {/* ─── Hero Section with Header Image ─── */}
      <ModernGradientHeader 
        themeColors={castingCall.theme_colors}
        hasHeaderImage={hasHeaderImage}
      >
        {/* Header Image Background */}
        {hasHeaderImage && (
          <HeaderImageBackground>
            <img src={castingCall.header_image} alt="Casting Call Header" />
          </HeaderImageBackground>
        )}

        <Container maxWidth="xl">
          <Box sx={{ py: { xs: 5, md: 7 }, position: "relative", zIndex: 3 }}>
            {/* Status Bar */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ mb: 4, flexWrap: "wrap", gap: 1 }}
            >
              {getStatusChip()}
              {getCallTypeBadge()}
              {isOwner && (
                <Chip
                  label="Your Casting Call"
                  variant="filled"
                  icon={<CheckCircleIcon fontSize="small" />}
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: "0.875rem", 
                    height: 34,
                    borderRadius: 10,
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    color: 'white',
                    border: `1px solid ${alpha('#fff', 0.3)}`,
                  }}
                />
              )}
            </Stack>

            {/* Main Content */}
            <Grid container spacing={4} alignItems="flex-end">
              {/* Left: Casting Call Info */}
              <Grid item xs={12} lg={8}>
                <Box>
                  {/* Title */}
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "-0.025em",
                      color: "common.white",
                      fontSize: { xs: "2.25rem", sm: "3rem", md: "3.75rem" },
                      lineHeight: 1.1,
                      wordBreak: "break-word",
                      textShadow: "0 4px 20px rgba(0,0,0,0.6)",
                      mb: 3,
                    }}
                  >
                    {castingCall.brief}
                  </Typography>

                  {/* Description */}
                  {castingCall.description && (
                    <Typography
                      variant="h6"
                      sx={{
                        opacity: 0.96,
                        fontWeight: 400,
                        color: "common.white",
                        lineHeight: 1.7,
                        fontSize: { xs: "1.125rem", md: "1.4rem" },
                        textShadow: "0 2px 12px rgba(0,0,0,0.6)",
                        maxWidth: "800px",
                      }}
                    >
                      {castingCall.description}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Right: Poster Info */}
              {castingCall.poster && (
                <Grid item xs={12} lg={4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2.5,
                      bgcolor: "rgba(255,255,255,0.18)",
                      backdropFilter: "blur(30px)",
                      borderRadius: 5,
                      p: 3.5,
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    }}
                  >
                    <Avatar
                      src={castingCall.poster.pfphref || ""}
                      alt={castingCall.poster.display_name}
                      sx={{
                        width: 76,
                        height: 76,
                        border: "3px solid rgba(255,255,255,0.4)",
                        boxShadow: theme.shadows[8],
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          fontWeight: 700,
                          mb: 0.5,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          letterSpacing: "1.2px",
                          display: "block",
                        }}
                      >
                        Posted By
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "common.white",
                          fontWeight: 700,
                          fontSize: "1.3rem",
                          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                        }}
                      >
                        {castingCall.poster.display_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Container>
      </ModernGradientHeader>

      {/* ─── Content Section ─── */}
      <Box sx={{ bgcolor: "background.default", pb: 10 }}>
        <Container maxWidth="xl" sx={{ mt: -5, position: "relative", zIndex: 2 }}>
          <Grid container spacing={4}>
            {/* Left Column - Details */}
            <Grid item xs={12} md={8}>
              <Stack spacing={4}>
                {/* Stats Grid */}
                <Grid container spacing={3}>
                  {/* Budget */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard elevation={0}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: "success.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MoneyIcon sx={{ fontSize: 34 }} />
                      </Box>
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: "text.secondary",
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: "0.8rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Budget
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                          ${castingCall.budget?.toLocaleString() || "TBD"}
                        </Typography>
                      </Box>
                    </InfoCard>
                  </Grid>

                  {/* Applicants */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard elevation={0}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: "info.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PeopleIcon sx={{ fontSize: 34 }} />
                      </Box>
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: "text.secondary",
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: "0.8rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {isOwner ? 'Applications Received' : 'Applicants'}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {castingCall.applicant_count || 0}
                        </Typography>
                      </Box>
                    </InfoCard>
                  </Grid>
                </Grid>

                {/* Supporting Images Gallery */}
                {hasSupportingImages && (
                  <Card
                    sx={{
                      borderRadius: 5,
                      boxShadow: theme.shadows[2],
                      overflow: "hidden",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ImageIcon sx={{ color: "primary.main" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, flex: 1 }}
                        >
                          Gallery
                        </Typography>
                        <Chip 
                          label={castingCall.supporting_images.length} 
                          size="small" 
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: "primary.main",
                            fontWeight: 700,
                          }}
                        />
                      </Stack>

                      <ImageList 
                        cols={3} 
                        gap={16}
                        sx={{ 
                          width: '100%',
                          m: 0,
                        }}
                      >
                        {castingCall.supporting_images.map((image, index) => (
                          <ImageListItem key={index}>
                            <GalleryImage onClick={() => handleImageClick(index + (hasHeaderImage ? 1 : 0))}>
                              <img
                                src={image}
                                alt={`Gallery image ${index + 1}`}
                                loading="lazy"
                              />
                            </GalleryImage>
                          </ImageListItem>
                        ))}
                      </ImageList>
                    </CardContent>
                  </Card>
                )}

                {/* Project Assets Section */}
                {organizersLoading ? (
                  <Card sx={{ borderRadius: 5, boxShadow: theme.shadows[2], border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CircularProgress size={24} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Loading assets...
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ) : organizers && organizers.total_count > 0 && (
                  <Card sx={{ borderRadius: 5, boxShadow: theme.shadows[2], overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FolderIcon sx={{ color: "primary.main" }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Project Assets
                          </Typography>
                        </Box>
                        <Badge 
                          badgeContent={organizers.total_assets} 
                          sx={{ 
                            '& .MuiBadge-badge': { 
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: "primary.main",
                            } 
                          }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                        Shared deliverables, brand assets, and collaboration files for this casting call.
                      </Typography>

                      <Stack spacing={4}>
                        {organizers.organizers.map((org) => (
                          <Box key={org.id}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                              {org.name}
                            </Typography>
                            
                            {org.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                                {org.description}
                              </Typography>
                            )}
                            
                            {org.assets_preview.length > 0 ? (
                              <Stack spacing={2.5}>
                                {org.assets_preview.map((asset) => (
                                  <AssetCard 
                                    key={asset.id}
                                    elevation={0}
                                    onClick={() => {
                                      if (!hasApplied && !isOwner) {
                                        alert('Apply to view full assets');
                                      }
                                    }}
                                  >
                                    {asset.type?.includes('image') && asset.url ? (
                                      <>
                                        <AssetPreview>
                                          <img src={asset.url} alt={asset.name} />
                                        </AssetPreview>
                                        <Box sx={{ p: 2.5 }}>
                                          <Typography variant="subtitle2" fontWeight={700}>
                                            {asset.name}
                                          </Typography>
                                        </Box>
                                      </>
                                    ) : (
                                      <Box sx={{ p: 2.5 }}>
                                        <Box display="flex" alignItems="center" gap={2}>
                                          <Box
                                            sx={{
                                              width: 52,
                                              height: 52,
                                              borderRadius: 2.5,
                                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {getFileIcon(asset.type)}
                                          </Box>
                                          <Box flex={1}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                              {asset.name}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    )}
                                  </AssetCard>
                                ))}
                                
                                {org.file_count > org.assets_preview.length && (
                                  <Typography variant="caption" color="text.secondary" align="center" sx={{ pt: 1 }}>
                                    +{org.file_count - org.assets_preview.length} more files
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Box 
                                sx={{ 
                                  p: 3.5, 
                                  textAlign: 'center',
                                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                                  borderRadius: 3,
                                  border: `1px dashed ${theme.palette.divider}`,
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  {org.file_count} file(s) available {isOwner ? 'in organizer' : 'after applying'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>

                      {!hasApplied && !isOwner && (
                        <Box 
                          sx={{ 
                            mt: 4, 
                            p: 3, 
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.info.main, 0.06),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            textAlign: 'center'
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, fontWeight: 500 }}>
                            <LockIcon sx={{ fontSize: 18 }} />
                            Apply to access all project assets ({organizers.total_assets} files)
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Details Card */}
                <Card
                  sx={{
                    borderRadius: 5,
                    boxShadow: theme.shadows[2],
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Platform/Work Type Chips */}
                    {isCreatorCall && castingCall.platforms && castingCall.platforms.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ 
                            mb: 2.5, 
                            fontWeight: 700, 
                            color: "text.primary",
                            fontSize: "1.05rem" 
                          }}
                        >
                          Platforms
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1.5}>
                          {castingCall.platforms.map((platform) => (
                            <Chip
                              key={platform}
                              icon={<PlatformIcon platform={platform} />}
                              label={platform}
                              variant="outlined"
                              sx={{ 
                                fontWeight: 600,
                                borderRadius: 3,
                                height: 38,
                                borderWidth: 1.5,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {!isCreatorCall && castingCall.work_types && castingCall.work_types.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ 
                            mb: 2.5, 
                            fontWeight: 700, 
                            color: "text.primary",
                            fontSize: "1.05rem" 
                          }}
                        >
                          Work Types
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1.5}>
                          {castingCall.work_types.map((workType) => (
                            <Chip
                              key={workType}
                              icon={<WorkIcon />}
                              label={workType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              variant="outlined"
                              sx={{ 
                                fontWeight: 600,
                                borderRadius: 3,
                                height: 38,
                                borderWidth: 1.5,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {(isCreatorCall && castingCall.platforms && castingCall.platforms.length > 0) ||
                    (!isCreatorCall && castingCall.work_types && castingCall.work_types.length > 0) ? (
                      <Divider sx={{ my: 4 }} />
                    ) : null}

                    {/* Full Description */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ 
                          mb: 3, 
                          fontWeight: 700, 
                          color: "text.primary",
                          fontSize: "1.05rem" 
                        }}
                      >
                        About This Opportunity
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ 
                          lineHeight: 1.8, 
                          whiteSpace: "pre-wrap",
                          color: "text.secondary",
                          fontSize: "1.05rem",
                        }}
                      >
                        {castingCall.description || "No additional details provided."}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right Column - Action Card */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 5,
                  boxShadow: theme.shadows[8],
                  position: { md: "sticky" },
                  top: { md: 100 },
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Check if user owns this casting call */}
                  {isOwner ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 52, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 700 }}>
                        This is Your Casting Call
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4.5, lineHeight: 1.8 }}>
                        You created this opportunity. View and manage applications from your dashboard.
                      </Typography>
                      <Stack spacing={2.5}>
                        <ModernButton
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          startIcon={<DashboardIcon />}
                          onClick={() => navigate(`/castingcall/${castingCall.id}/manage`)}
                          sx={{
                            height: 56,
                            fontSize: '1.05rem',
                          }}
                        >
                          Manage Applications
                        </ModernButton>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          size="large"
                          startIcon={<ShareIcon />}
                          onClick={handleShareLink}
                          sx={{
                            borderRadius: 3,
                            height: 52,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderWidth: 1.5,
                            '&:hover': {
                              borderWidth: 1.5,
                            }
                          }}
                        >
                          Share This Opportunity
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    /* Use the CastingCallApplication component for non-owners */
                    <CastingCallApplication
                      castingCall={castingCall}
                      isAuthenticated={isAuthorized || isCreatorAuthorized}
                      isCreatorAuthorized={isCreatorAuthorized}
                      hasApplied={hasApplied}
                      onSuccess={() => {
                        setSuccessDialogOpen(true);
                        refetch();
                      }}
                      onLoginRequired={() => setLoginDialogOpen(true)}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── Image Viewer Dialog ─── */}
      <StyledDialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: 'rgba(0,0,0,0.96)',
            borderRadius: 4,
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={() => setImageViewerOpen(false)}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              zIndex: 10,
              width: 48,
              height: 48,
            }}
          >
            <CloseIcon />
          </IconButton>

          {allImages.length > 1 && (
            <IconButton
              onClick={handlePreviousImage}
              sx={{
                position: 'absolute',
                left: 20,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                width: 48,
                height: 48,
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}

          <Box
            component="img"
            src={allImages[currentImageIndex]}
            alt={`Image ${currentImageIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
          />

          {allImages.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 20,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                width: 48,
                height: 48,
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}

          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 3,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {currentImageIndex + 1} / {allImages.length}
            </Typography>
          </Box>
        </DialogContent>
      </StyledDialog>

      {/* ─── Login Dialog ─── */}
      <StyledDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        PaperProps={{
          sx: { maxWidth: 500 },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <LoginIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Join Blitz to Apply
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            Connect with opportunities and showcase your work
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Typography align="center" sx={{ color: "text.secondary", lineHeight: 1.8, px: 2 }}>
            You must be logged in to apply to casting calls. Create an account to get started or log in if you already have one.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 2, px: 4, pb: 4, justifyContent: "center", flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button 
            onClick={() => navigate(routes.login)} 
            variant="outlined"
            size="large"
            startIcon={<LoginIcon />}
            sx={{ 
              borderRadius: 3,
              px: 4,
              height: 48,
              fontWeight: 600,
              borderWidth: 1.5,
              '&:hover': {
                borderWidth: 1.5,
              }
            }}
          >
            Log In
          </Button>
          <ModernButton
            onClick={() => navigate(
              castingCall.global_user_id 
                ? `/invite/${castingCall.global_user_id}` 
                : routes.onboard
            )}
            variant="contained"
            size="large"
            startIcon={<PersonAddIcon />}
            sx={{ height: 48 }}
          >
            Create Account
          </ModernButton>
        </DialogActions>
      </StyledDialog>

      {/* ─── Success Dialog ─── */}
      <StyledDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        PaperProps={{
          sx: { maxWidth: 500 },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 52, color: "success.main" }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
            Application Successful!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography align="center" sx={{ color: "text.secondary", lineHeight: 1.8, px: 3 }}>
            Your application has been submitted successfully. The poster will review
            your application and get back to you soon.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4, pt: 2 }}>
          <ModernButton
            onClick={() => setSuccessDialogOpen(false)}
            variant="contained"
            color="success"
            size="large"
            sx={{ px: 5, height: 48 }}
          >
            Got It!
          </ModernButton>
        </DialogActions>
      </StyledDialog>
    </>
  );
}